import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import styles from "./DemoPage.module.css";

const exchanges = [
  {
    prompt: `We performed co-immunoprecipitation under native conditions and confirmed COQ9 physically interacts with COQ7. Structural comparison of COQ9 against TetR family crystal structures is available in the literature. In vitro binding assays show COQ9 binds hydrophobic lipid analogues with micromolar affinity. Given these findings and what you know about the physical properties of CoQ biosynthetic intermediates and the spatial organisation of the synthome — what is the precise molecular function of COQ9?`,
    response: [
      "HYPOTHESES",
      "1. COQ9 functions as a structural scaffold that stabilizes the CoQ biosynthetic complex, enabling spatial organization of enzymes.",
      "2. COQ9 acts as a molecular shuttle, facilitating the translocation of CoQ intermediates between compartment-specific enzymes.",
      "PREDICTIONS",
      "1. COQ9 depletion will disrupt the physical association of CoQ biosynthetic enzymes, leading to reduced CoQ accumulation despite intact enzymatic activities.",
      "2. Overexpression of COQ9 will enhance the rate of CoQ biosynthesis when measured via HPLC quantification of Q intermediates.",
      "3. COQ9 will co-purify with key CoQ biosynthetic enzymes under native conditions, confirming its role in complex assembly.",
      "EXPERIMENTS",
      "1. Perturbation: Deplete COQ9 using CRISPR-Cas9 in yeast coq10 null mutants. Control: Non-targeting guide RNA. Readouts: Measure CoQ levels via HPLC and assess enzyme proximity using BRET assays. Expected outcome: Reduced CoQ levels and disrupted enzyme clustering support Hypothesis 1. Decision rule: If CoQ levels correlate with enzyme clustering integrity, accept Hypothesis 1.",
      "2. Perturbation: Express tagged COQ9 variants (wild-type vs. truncation mutants) in mammalian cells. Control: Empty vector. Readouts: Quantify CoQ intermediates via mass spectrometry and measure intracellular trafficking rates. Expected outcome: Wild-type COQ9 enhances intermediate flux while truncation mutants fail to rescue, supporting Hypothesis 2. Decision rule: A significant difference in flux rates confirms Hypothesis 2.",
      "BELIEF UPDATE RULE",
      "If Experiment 1 shows COQ9 depletion reduces enzyme clustering without affecting individual enzymatic activity, I favor Hypothesis 1 and prioritize structural stabilization as COQ9's role. If Experiment 2 demonstrates COQ9 is required for intermediate transport but not for complex assembly, I favor Hypothesis 2 and investigate trafficking pathways. If neither experiment yields clear results, I hypothesize COQ9 may act as a chaperone-like protein that protects intermediates from degradation.",
    ],
  },
  {
    prompt: `Good. Your Hypothesis 2 is directionally correct. Now consider this additional data: purified recombinant COQ9 binds fluorescent lipid analogues with micromolar affinity in vitro. Structural modelling of COQ9 against known TetR family crystal structures reveals it has the conserved hydrophobic ligand-binding pocket characteristic of TetR proteins but completely lacks the helix-turn-helix DNA-binding domain residues that are conserved across all known DNA-binding TetR regulators. COQ9 physically interacts with COQ7 by co-immunoprecipitation under native conditions. COQ7 is the diiron hydroxylase that converts DMQ10 to DMQH2 — the penultimate step in CoQ10 biosynthesis. The CoQ biosynthetic intermediate DMQ10 has a 50-angstrom isoprenyl tail that is extremely hydrophobic and cannot diffuse freely through the aqueous mitochondrial matrix. The CoQ synthome concentrates all biosynthetic enzymes spatially at the inner mitochondrial membrane but the active site of COQ7 faces the aqueous matrix. Given all of these findings together, what is the precise molecular function of COQ9, why is a carrier protein physically necessary for this biosynthetic step, and what specific structural feature of COQ9 performs this function?`,
    response: [
      "COQ9 functions as a lipid-chaperone protein, specifically evolved to solubilize and transport the highly hydrophobic CoQ biosynthetic intermediate DMQ10 across the aqueous mitochondrial matrix.",
      "While retaining the conserved hydrophobic ligand-binding pocket of the TetR family, COQ9 has lost the DNA-binding helix-turn-helix motif entirely, redirecting its structural capacity toward lipid recognition rather than nucleic acid interactions.",
      "The extreme hydrophobicity of DMQ10 (logD > 10 at physiological pH) renders it immobile in the polar matrix environment, necessitating a dedicated carrier.",
      "COQ9's dual properties — binding DMQ10 with micromolar affinity while maintaining weak interactions with both the membrane-bound CoQ synthome and the aqueous-facing active site of COQ7 — position it as a transient bridge: it extracts DMQ10 from the membrane interface, carries it through the matrix via its hydrophobic pocket, and delivers it to COQ7's catalytic surface.",
      "This spatial relay mechanism is indispensable because COQ7's active site is sterically shielded from direct access to the membrane by surrounding synthome proteins.",
    ],
  },
  {
    prompt: `Excellent. One final question. Given this lipid shuttle mechanism — COQ9 extracting DMQ10 from the membrane, carrying it through the matrix, and presenting it to COQ7 — design one experiment that has not been described in the published literature that would definitively confirm the directional handoff model and distinguish it from a simpler model where COQ9 merely increases DMQ10 solubility without directed delivery to COQ7.`,
    response: [
      "To definitively distinguish between the directional handoff model and the solubility-enhancement model, perform a pulsed-labeling experiment combined with proximity ligation assays.",
      "Label DMQ10 with a biotin-conjugated fatty acid analog immediately before adding COQ9 to isolated mitochondria. After a defined time interval, quench the reaction and isolate COQ7-bound DMQ10 products via streptavidin pull-down.",
      "Quantify the amount of DMQ10 delivered to COQ7 relative to total DMQ10 in the supernatant. As controls, repeat the experiment in the absence of COQ9 and in the presence of a competitive lipid analog that displaces DMQ10 from COQ9's binding pocket.",
      "If COQ9 functions as a directed shuttle, the percentage of DMQ10 delivered to COQ7 will correlate with COQ9 concentration and be significantly reduced in the absence of COQ9. Conversely, if COQ9 merely enhances solubility, both models will yield similar delivery rates, as DMQ10 will remain available in solution regardless of COQ9's presence.",
    ],
  },
];

export default function Coq9Demo() {
  return (
    <div className={styles.page}>
      <div className={styles.background}>
        <div className={styles.glow} />
      </div>
      <div className={styles.container}>
        <motion.div
          intial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/" className={styles.backLink}>
            &larr; Back to Home
          </Link>

          <div className={styles.header}>
            <span className={styles.label}>Research Session</span>
            <h1 className={styles.title}>
              COQ9 - The{" "}
              <span className={styles.titleHighlight}>Lipid Shuttle</span>{" "}
              Mechanism
            </h1>
            <p className={styles.subtitle}>
              A real session with MitoAgent tracing the molecular function of
              COQ9 from competing hypotheses to a novel experimental design —
              across three rounds of data.
            </p>
          </div>

          <div className={styles.chat}>
            {exchanges.map((exchange, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={styles.exchange}
              >
                {/*Round label */}
                <div className={styles.roundLabel}>Round {i + 1}</div>

                {/* Researcher message */}
                <div className={styles.messageRow}>
                  <div className={styles.avatar}>R</div>
                  <div className={styles.bubbleResearcher}>
                    <div className={styles.senderLabel}>Researcher</div>
                    <p>{exchange.prompt}</p>
                  </div>
                </div>

                {/* MitoAgent response */}
                <div className={`${styles.messageRow} ${styles.agentRow}`}>
                  <div className={styles.avatar}>M</div>
                  <div className={styles.bubbleAgent}>
                    <div className={styles.senderLabel}>MitoAgent </div>
                    <div className={styles.responseBody}>
                      {exchange.response.map((para, j) => (
                        <p key={j} className={styles.responsePara}>
                          {para}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className={styles.cta}>
            <p className={styles.ctaText}>
              Want MitoAgent reasoning over your data?
            </p>
            <Link to="/playground" className={styles.ctaButton}>
              Apply for Beta Access &rarr;
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
